import FilterPanel from '@components/FilterPanel/FilterPanel';
import moment from 'moment';
import { PlusOutlined, ArrowDownOutlined } from '@ant-design/icons';
import useGuards from '@hooks/useGuards';
import { openModal } from '@stores/modals';
import { Button, DatePicker, Form, Radio, RadioChangeEvent, Select } from 'antd';
import { FunctionComponent, useCallback } from 'react';
import css from './PromoFilter.module.less';
import useTranslate from '@hooks/useTranslate';
import componentContent from './PromoFilters.content.json';
import localeWeightStore, { LOCALE, localeWeightMap, setLocaleWeight } from '@stores/localeWeight';
import { useStore } from 'effector-react';
import { DATE_FORMAT } from '@config/format';
import activity from '@stores/activity';
import { PromotionActivityStatus } from '@interfaces/IPromotionActivity';
import { RangePickerProps } from 'antd/lib/date-picker';

interface PromoFilterProps {}

const { Option } = Select;
const { RangePicker } = DatePicker;

const PromoFilter: FunctionComponent<PromoFilterProps> = () => {
  const { allowChange } = useGuards();
  const content = useTranslate(componentContent);
  const localeName = useStore(localeWeightStore);
  const { filters, setFilters, pagination, setPagination } = useStore(activity);

  const createPromotionActivity = useCallback(() => {
    openModal({ name: 'activity' });
  }, []);

  const changeTypeFilter = (event: RadioChangeEvent) => {
    setFilters({ ...filters, statusFilter: event.target.value });
  };

  const onChangeDateRange = useCallback(
    (range: RangePickerProps['value']) => {
      const [dateStart, dateEnd] = range ?? [];
      let afterFilter, beforeFilter;

      if (dateStart) afterFilter = dateStart.startOf('day').format();
      if (dateEnd) beforeFilter = dateEnd.startOf('day').format();
      setFilters({
        ...filters,
        afterFilter,
        beforeFilter,
      });
    },
    [filters],
  );

  const onChangeLocale = useCallback(
    (localeFilter: LOCALE) => {
      setLocaleWeight(localeFilter);
      setFilters({ ...filters, localeFilter });
    },
    [filters],
  );

  const clearFilters = () => {
    setLocaleWeight(LOCALE.ALL);
    setPagination({ ...pagination, current: 1 });
    setFilters({});
  };

  return (
    <Form layout='vertical'>
      <div className={css.filters}>
        <Form.Item className={css.headerButton}>
          {allowChange ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={createPromotionActivity}
              className={css.headerButton}
            >
              {content.add_notification}
            </Button>
          ) : null}
          <Button
            type='primary'
            icon={<ArrowDownOutlined />}
            className={css.headerButton}
          >
            {content.download_report}
          </Button>
        </Form.Item>

        <FilterPanel
          actions={
            <Button
              htmlType='button'
              onClick={clearFilters}
            >
              {content.clear}
            </Button>
          }
        >
          <Form.Item label={content.choose_tab}>
            <Radio.Group
              onChange={changeTypeFilter}
              value={filters?.statusFilter}
              className={css.statusTabBar}
            >
              <Radio.Button value={PromotionActivityStatus.ACTIVE}>
                {content.active_status}
              </Radio.Button>
              <Radio.Button value={PromotionActivityStatus.PLANNED}>
                {content.planned_status}
              </Radio.Button>
              <Radio.Button value={PromotionActivityStatus.ENDED}>
                {content.ended_status}
              </Radio.Button>
              <Radio.Button value={PromotionActivityStatus.ERROR}>{content.all}</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item label={content.choose_date}>
            <RangePicker
              className={css.dateRangeSelector}
              format={DATE_FORMAT}
              allowEmpty={[true, true]}
              value={[
                filters.afterFilter ? moment(filters.afterFilter) : null,
                filters.beforeFilter ? moment(filters.beforeFilter) : null,
              ]}
              placeholder={[content.rangePlaceholderFrom, content.rangePlaceholderTo]}
              onChange={onChangeDateRange}
            />
          </Form.Item>

          <Form.Item label={content.choose_locale}>
            <Select
              style={{ width: 120 }}
              value={localeName}
              onChange={onChangeLocale}
            >
              {localeWeightMap &&
                Object.entries(localeWeightMap).map(([key, value]) => {
                  return (
                    <Option
                      value={key}
                      key={key}
                    >
                      {value.name}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
        </FilterPanel>
      </div>
    </Form>
  );
};

export default PromoFilter;
